import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CertifiedBadge } from "@/components/CertifiedBadge";
import { Sparkles, Shield, Heart, Compass } from "lucide-react";

export default function About() {
  return (
    <div className="scent-wave">
      <section className="max-w-4xl mx-auto px-6 md:px-10 py-24">
        <div className="text-[10px] tracking-[0.28em] uppercase text-emerald-700 font-semibold">Our Story</div>
        <h1 className="mt-3 font-serif text-5xl md:text-6xl tracking-tight text-[#0A1128] leading-[1.05]">
          Every stay <br /> should feel <span className="italic text-emerald-600">fresh</span>.
        </h1>
        <p className="mt-6 text-lg text-slate-600 max-w-2xl leading-relaxed">
          Guest Smell™ was founded on a simple truth: photos can lie, reviews are noisy, but the way a room actually feels — the air, the fabric, the freshness — is the moment that decides whether a stay is remembered fondly or regretted.
        </p>
      </section>

      <section className="bg-white py-20 border-y border-slate-200">
        <div className="max-w-4xl mx-auto px-6 md:px-10 grid md:grid-cols-3 gap-10 items-start">
          <div className="md:col-span-1">
            {/* Founder portrait — Chalounda Williams, founder of Guest Smell™.
                To replace with a different photo later, drop the new file into
                /app/frontend/public/founder.jpg (any JPG / PNG / WEBP works). */}
            <div className="relative rounded-3xl aspect-[3/4] overflow-hidden shadow-2xl bg-[#0A1128]">
              <img
                src="/founder.jpg"
                alt="Chalounda Williams, Founder of Guest Smell"
                className="absolute inset-0 w-full h-full object-cover"
                style={{ objectPosition: "center 25%" }}
                loading="eager"
                data-testid="founder-photo"
              />
              {/* Bottom-fade gradient so the nameplate stays readable */}
              <div
                className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-[#0A1128] via-[#0A1128]/85 to-transparent"
                aria-hidden="true"
              />
              {/* Gold accent bar at top */}
              <div className="absolute top-0 inset-x-0 h-[3px] bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" aria-hidden="true" />
              {/* Nameplate */}
              <div className="absolute inset-x-0 bottom-0 p-6">
                <div className="text-[10px] tracking-[0.32em] uppercase text-[#D4AF37] font-bold">
                  Founder
                </div>
                <div className="mt-2 font-serif text-2xl md:text-3xl text-white leading-tight">
                  Chalounda<br />Williams
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <span className="h-px w-8 bg-[#D4AF37]" aria-hidden="true" />
                  <span className="text-[9px] tracking-[0.32em] uppercase text-slate-200 font-semibold">
                    Guest Smell™
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="md:col-span-2">
            <div className="text-[10px] tracking-[0.28em] uppercase text-emerald-700 font-semibold">Founder&apos;s note</div>
            <h2 className="mt-2 font-serif text-3xl text-[#0A1128] tracking-tight">Building the standard.</h2>
            <p className="mt-4 text-slate-700 leading-relaxed">
              After too many stays where the photos didn&apos;t match the smell, I set out to build the trust layer hospitality was missing. Guest Smell™ starts with vacation rentals — Airbnb, Vrbo, Booking.com — and extends into hotels, senior living, cruise, and luxury real estate. Anywhere freshness matters, we&apos;ll be the certification guests look for.
            </p>
            <p className="mt-3 text-slate-700 leading-relaxed">
              We measure five signals continuously — Freshness Monitoring, Air Quality, Humidity, Smoke Detection, and Cleaning Verification — and roll them into one honest score. Every certified stay carries a public verification page. Every host gets a marketing kit and a growth dashboard.
            </p>
            <p className="mt-3 text-slate-700 leading-relaxed">
              This is only the beginning. See it. Smell it. Book it.™
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 md:px-10 py-24">
        <div className="text-[10px] tracking-[0.28em] uppercase text-emerald-700 font-semibold">What we stand for</div>
        <h2 className="mt-2 font-serif text-4xl md:text-5xl text-[#0A1128] tracking-tight">Our values.</h2>
        <div className="mt-10 grid md:grid-cols-2 gap-6">
          {[
            { icon: Shield,   title: "Honesty above all",        copy: "A score you can trust. Verifiable. Public. Never inflated for placement." },
            { icon: Sparkles, title: "Luxury in the details",    copy: "Freshness is invisible but everywhere. We treat it with the reverence it deserves." },
            { icon: Compass,  title: "Standards, not opinions", copy: "Reviews are subjective. We built an objective standard the industry can rally around." },
            { icon: Heart,    title: "Hosts & guests, together", copy: "One badge that helps hosts earn more bookings and helps guests book with confidence." },
          ].map((v) => (
            <div key={v.title} className="rounded-2xl border border-slate-200 bg-white p-6">
              <v.icon className="w-6 h-6 text-emerald-600" />
              <h3 className="mt-3 font-serif text-xl text-[#0A1128]">{v.title}</h3>
              <p className="mt-2 text-sm text-slate-600" dangerouslySetInnerHTML={{__html: v.copy}} />
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#0A1128] text-white py-20">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <CertifiedBadge tier="premium" />
          <h2 className="mt-6 font-serif text-3xl md:text-4xl tracking-tight">Ready to wear the badge?</h2>
          <p className="mt-3 text-slate-300 max-w-xl mx-auto">Certify your property, unlock the marketing kit, and start converting freshness into bookings.</p>
          <div className="mt-8 flex justify-center gap-3 flex-wrap">
            <Link to="/register"><Button size="lg" className="rounded-full bg-[#D4AF37] hover:bg-[#c19d2e] text-[#0A1128] px-8">Become a Host</Button></Link>
            <Link to="/contact"><Button size="lg" variant="outline" className="rounded-full border-white/30 text-white hover:bg-white/10 px-8">Contact us</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
